/**
 * useResolvedDefaultCalendarId
 *
 * Reads the user's stored default-calendar preference and validates it
 * against the provided writable-calendars list. Returns null if no stored
 * preference exists or the stored ID is no longer writable — callers should
 * fall back to their existing "first writable" behavior in that case.
 *
 * Re-resolves on screen focus so changes made on the Settings screen take
 * effect immediately when returning to the calling screen.
 */

import { useCallback, useEffect, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useDefaultCalendarManager } from '@/hooks/use-scoped-managers';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

export function useResolvedDefaultCalendarId(
  writableCalendars: DeviceCalendar[],
): string | null {
  const manager = useDefaultCalendarManager();
  const [resolvedId, setResolvedId] = useState<string | null>(null);

  const resolve = useCallback(async (): Promise<string | null> => {
    if (!manager || writableCalendars.length === 0) {
      return null;
    }
    return manager.getDefault(writableCalendars);
  }, [manager, writableCalendars]);

  useEffect(() => {
    let cancelled = false;
    resolve().then((id) => {
      if (!cancelled) setResolvedId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [resolve]);

  // Re-resolve whenever the owning screen regains focus (e.g. user returns
  // from the Settings → Default Calendar screen).
  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      resolve().then((id) => {
        if (!cancelled) setResolvedId(id);
      });
      return () => {
        cancelled = true;
      };
    }, [resolve]),
  );

  return resolvedId;
}
